import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Search, Filter, ExternalLink, Package, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SuperAdminRestaurantsPage() {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchRestaurants();
  }, [planFilter]);

  const fetchRestaurants = async () => {
    const token = localStorage.getItem('superadmin_token');
    if (!token) {
      navigate('/orderlab-admin/login');
      return;
    }

    try {
      let url = `${BACKEND_URL}/api/superadmin/restaurants`;
      const params = [];
      if (planFilter) params.push(`plan=${planFilter}`);
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (params.length > 0) url += `?${params.join('&')}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setRestaurants(data.restaurants || []);
      } else if (response.status === 401) {
        navigate('/orderlab-admin/login');
      }
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRestaurants();
  };

  const getPlanBadge = (plan) => {
    const styles = {
      free: 'bg-slate-50 text-slate-700 border-slate-200',
      pro: 'bg-blue-50 text-blue-700 border-blue-200',
      plus: 'bg-purple-50 text-purple-700 border-purple-200'
    };
    return styles[plan] || styles.free;
  };

  const getStatusBadge = (status) => {
    if (status === 'Actif') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (status === 'Inactif') return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-red-50 text-red-700 border-red-200';
  };

  const filteredRestaurants = restaurants.filter(r =>
    r.name.toLowerCase().includes(search.toLowerCase()) ||
    r.email.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold mb-2">Restaurants</h1>
          <p className="text-muted-foreground">{filteredRestaurants.length} restaurant(s) au total</p>
        </div>
      </div>

      {/* Search & Filters */}
      <Card className="p-4">
        <form onSubmit={handleSearch} className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Rechercher par nom ou email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            {['', 'free', 'pro', 'plus'].map(plan => (
              <Button
                key={plan || 'all'}
                type="button"
                variant={planFilter === plan ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPlanFilter(plan)}
                data-testid={`filter-${plan || 'all'}`}
              >
                {plan ? plan.toUpperCase() : 'Tous'}
              </Button>
            ))}
          </div>
        </form>
      </Card>

      {/* Table */}
      {filteredRestaurants.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <p className="text-muted-foreground">Aucun restaurant trouvé</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="restaurants-table">
              <thead className="bg-muted/30 border-b">
                <tr>
                  <th className="text-left p-4 font-semibold text-sm">Restaurant</th>
                  <th className="text-left p-4 font-semibold text-sm">Plan</th>
                  <th className="text-left p-4 font-semibold text-sm">Statut</th>
                  <th className="text-center p-4 font-semibold text-sm">Commandes</th>
                  <th className="text-center p-4 font-semibold text-sm">Dernière activité</th>
                  <th className="text-right p-4 font-semibold text-sm">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRestaurants.map((restaurant, idx) => (
                  <tr
                    key={restaurant.id}
                    className="border-b hover:bg-muted/20 transition-colors"
                    data-testid={`restaurant-row-${idx}`}
                  >
                    <td className="p-4">
                      <div>
                        <p className="font-medium">{restaurant.name}</p>
                        <p className="text-sm text-muted-foreground">{restaurant.email}</p>
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge className={`${getPlanBadge(restaurant.plan)} border`} data-testid={`plan-badge-${idx}`}>
                        {restaurant.plan.toUpperCase()}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <Badge className={`${getStatusBadge(restaurant.status)} border`} data-testid={`status-badge-${idx}`}>
                        {restaurant.status}
                      </Badge>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Package className="w-4 h-4 text-muted-foreground" />
                        <span className="font-medium tabular-nums">{restaurant.total_orders || 0}</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        {restaurant.last_login_at
                          ? new Date(restaurant.last_login_at).toLocaleDateString('fr-FR', { month: 'short', day: 'numeric' })
                          : 'Jamais'}
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <Link to={`/orderlab-admin/restaurants/${restaurant.id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          data-testid={`view-details-${idx}`}
                        >
                          Voir détails
                          <ExternalLink className="w-3 h-3 ml-2" />
                        </Button>
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
